import { ElementType, ComponentPropsWithoutRef, ReactNode } from 'react';
import { diMaskProps } from 'lib/analytics/diMask';

/**
 * Props for {@link Masked}, polymorphic over the rendered element/component
 * via the `as` prop (defaults to `span`). Extends whatever props the target
 * element/component accepts (e.g. `as="a"` accepts `href`, `as={Typography}`
 * accepts MUI `Typography` props), minus `as` itself.
 */
export type MaskedProps<T extends ElementType> = {
  /** The element or component to render. Defaults to `span`. */
  as?: T;
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children'>;

const DEFAULT_ELEMENT = 'span';

/**
 * Generic, polymorphic display-side masking primitive for Medallia DXA
 * (Decibel Insight) PII masking (CD-8245).
 *
 * Renders the given `as` element/component (default `span`) with the
 * valueless `data-di-mask` attribute applied, via the single shared helper
 * `lib/analytics/diMask.ts`. Caller props are spread first, then
 * `diMaskProps()`, so the mask attribute can never be accidentally
 * overridden by a caller-supplied `data-di-mask`.
 *
 * Every read-only PII **display** site should render its value through this
 * primitive (directly, or via one of the semantic wrappers in this
 * directory — `CaseRef`, `SupplierName`, etc.) rather than spreading
 * `data-di-mask` inline. This component is display-only — form inputs are
 * masked via the shared field wrappers (`inputProps` / res-components
 * source), not via this component.
 *
 * Supports `dangerouslySetInnerHTML` (e.g. for sanitised free-text bodies)
 * exactly as native elements do — the mask attribute lands on the rendering
 * element itself, never inside the HTML string. As with native React
 * elements, do not pass both `children` and `dangerouslySetInnerHTML`.
 *
 * @example
 * <Masked>{customerName}</Masked> // <span data-di-mask>...</span>
 * @example
 * <Masked as="pre" dangerouslySetInnerHTML={{ __html: sanitisedHtml }} />
 * @example
 * <Masked as={Typography} noWrap title={label}>{label}</Masked>
 *
 * NOTE: this is a plain function component and does NOT forward refs. If a
 * ref-bearing element must be masked, apply `diMaskProps()` / `data-di-mask`
 * to that element directly rather than wrapping it in `Masked`.
 */
export function Masked<T extends ElementType = typeof DEFAULT_ELEMENT>({
  as,
  children,
  ...rest
}: MaskedProps<T>) {
  const Component = (as ?? DEFAULT_ELEMENT) as ElementType;

  return (
    <Component {...rest} {...diMaskProps()}>
      {children}
    </Component>
  );
}

export default Masked;
